import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Hospital, MapPin, Loader2, ArrowRight, Copy, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { referralApi } from "@/lib/api";
import logoWide from "@/assets/trata-health-logo-wide.png";

const PENDING_KEY = "trata_pending_referral";

export default function ReferralLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hospital, setHospital] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  const cleanCode = (code || "").trim().toUpperCase();

  useEffect(() => {
    let active = true;
    referralApi
      .resolve(cleanCode)
      .then((res) => {
        if (!active) return;
        setHospital(res.data?.hospital || null);
        // Persist so the code survives the signup flow / a later app install on this browser.
        try {
          localStorage.setItem(PENDING_KEY, JSON.stringify({ code: cleanCode, ts: Date.now() }));
        } catch (e) {
          // ignore
        }
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [cleanCode]);

  const continueToApp = () => navigate(`/auth?ref=${encodeURIComponent(cleanCode)}`);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(cleanCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.info(`Your code is ${cleanCode}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-accent/40 to-background flex flex-col items-center justify-center px-5 py-10">
      <img src={logoWide} alt="Trata Health" className="h-11 md:h-14 w-auto object-contain mb-8" />

      <Card className="w-full max-w-md rounded-3xl p-7" data-testid="referral-landing-card">
        {loading ? (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-3 text-sm text-muted-foreground">Looking up your hospital…</p>
          </div>
        ) : error || !hospital ? (
          <div className="text-center py-4">
            <div className="h-14 w-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
              <Hospital className="h-7 w-7 text-destructive" />
            </div>
            <p className="mt-4 font-semibold">Invalid or expired code</p>
            <p className="text-sm text-muted-foreground mt-1">
              We couldn&apos;t find a partner hospital for <span className="font-mono font-semibold">{cleanCode}</span>. You can still continue and enter a code during sign up.
            </p>
            <Button className="mt-5 rounded-2xl w-full" onClick={() => navigate("/auth")} data-testid="referral-continue-noresolve">
              Continue to Trata Health <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <div className="h-16 w-16 rounded-2xl bg-accent flex items-center justify-center mx-auto overflow-hidden">
              {hospital.logoUrl ? (
                <img src={hospital.logoUrl} alt={hospital.name} className="h-full w-full object-cover" />
              ) : (
                <Hospital className="h-8 w-8 text-primary" />
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-4">You&apos;ve been invited by</p>
            <h1 className="text-xl font-semibold mt-1" data-testid="referral-hospital-name">{hospital.name}</h1>
            {(hospital.city || hospital.address) && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {hospital.city || hospital.address}
              </p>
            )}

            <button
              onClick={copyCode}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm font-mono font-semibold"
              data-testid="referral-code-chip"
            >
              {cleanCode}
              {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>

            <Button className="mt-6 rounded-2xl w-full h-12" onClick={continueToApp} data-testid="referral-continue-button">
              Continue & link {hospital.name.split(" ")[0]} <ArrowRight className="h-4 w-4" />
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              We&apos;ll link this hospital to your account so your care stays connected. You can confirm this during sign up.
            </p>
            <p className="text-[11px] text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> DPDP-compliant · you control what you share
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
